import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@amelio/ui-library';

import BarChart from '../BarChart/BarChart';
import {
    BAR_MIN_WIDTH_PX,
    CHART_HEIGHT_PX,
    TOOLTIP_DELAY_MS,
    Y_AXIS_WIDTH_CLASS,
} from '../BarChart/barChart.constants';

import type { BarChartProps, BarChartValue } from '../BarChart/BarChart';
import type { ReactNode } from 'react';

import { useScrollSync } from '@/hooks';

export interface BarChartTooltipOverlayProps extends BarChartProps {
    /** Render function for tooltip content per bar */
    renderTooltipContent: (barValue: BarChartValue, index: number) => ReactNode;
    /** Min width per bar for overlay sizing — defaults to 144px */
    barWidthPx?: number;
}

/**
 * BarChartTooltipOverlay
 *
 * Wraps a BarChart with an absolute-positioned tooltip overlay.
 * Each bar gets an invisible hover zone that triggers a tooltip.
 * Scroll is synchronized between the overlay and the underlying chart.
 */
const BarChartTooltipOverlay = ({
    renderTooltipContent,
    barWidthPx = BAR_MIN_WIDTH_PX,
    ...barChartProps
}: BarChartTooltipOverlayProps) => {
    const { values, yValues, maxYValue, minYValue = 0 } = barChartProps;
    const { chartContainerRef, tooltipOverlayRef, handleTooltipScroll } = useScrollSync();

    const effectiveMaxValue =
        maxYValue || Math.max(...(yValues ?? []), ...values.map((v) => v.value));
    const range = effectiveMaxValue - minYValue;

    const calculateBarHeight = (value: number): number => {
        if (range === 0) return 0;

        return ((value - minYValue) / range) * 100;
    };

    return (
        <TooltipProvider>
            <div
                ref={chartContainerRef}
                className={'relative'}
            >
                <BarChart {...barChartProps} />

                <div
                    className={'pointer-events-none absolute inset-x-0 top-0 flex'}
                    style={{ height: `${CHART_HEIGHT_PX}px` }}
                >
                    <div className={Y_AXIS_WIDTH_CLASS} />

                    <div
                        ref={tooltipOverlayRef}
                        className={'pointer-events-auto -mx-9 max-w-full flex-1 overflow-x-auto'}
                        style={{ scrollbarWidth: 'none' }}
                        onScroll={handleTooltipScroll}
                    >
                        <div
                            className={'pointer-events-auto relative flex flex-col px-9'}
                            style={{ minWidth: `${values.length * barWidthPx}px` }}
                        >
                            <div
                                className={'relative flex flex-col'}
                                style={{ height: `${CHART_HEIGHT_PX}px` }}
                            >
                                <div
                                    className={
                                        'relative flex h-full items-end justify-around gap-1 pr-4'
                                    }
                                >
                                    {values.map((barValue, index) => {
                                        const barHeightPercent = calculateBarHeight(barValue.value);

                                        return (
                                            <div
                                                key={index}
                                                className={
                                                    'flex min-w-0 flex-1 flex-col items-center'
                                                }
                                            >
                                                <div
                                                    className={'relative flex w-16 items-end'}
                                                    style={{
                                                        height: `${CHART_HEIGHT_PX}px`,
                                                    }}
                                                >
                                                    <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
                                                        <TooltipTrigger asChild>
                                                            <div
                                                                className={
                                                                    'min-h-[3px] w-full cursor-pointer'
                                                                }
                                                                style={{
                                                                    height: `${barHeightPercent}%`,
                                                                }}
                                                                data-testid={`${barChartProps.dataTestId}-bar-hover-${index}`}
                                                            />
                                                        </TooltipTrigger>

                                                        <TooltipContent
                                                            side={'right'}
                                                            data-testid={`${barChartProps.dataTestId}-tooltip-${index}`}
                                                        >
                                                            {renderTooltipContent(barValue, index)}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
};

export { BarChartTooltipOverlay };
export default BarChartTooltipOverlay;
